import * as React from 'react';

import cn from '@/utils/cn';

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  // The scroll container. `TableHeader` is `sticky top-0`, but sticky resolves
  // against the nearest scrolling ancestor - which is this wrapper, and it has
  // no height bound, so it never scrolls and the sticky header was dead code
  // on every page. Pass a `max-h-*` here to make the header actually stick.
  containerClassName?: string;
}

export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerClassName, ...props }, ref) => (
    <div className={cn('relative w-full overflow-auto', containerClassName)}>
      <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  ),
);
Table.displayName = 'Table';

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('sticky top-0 z-10 bg-card [&_tr]:border-b', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    scope={props.scope ?? 'col'}
    className={cn(
      // The responsive step is a CSS custom property, not `sm:h-7`: twMerge
      // cannot dedupe a `sm:`-prefixed base class against a caller's unprefixed
      // `h-*`, so the base would win at >=640px. One unprefixed
      // `h-[var(--table-head-h)]` conflicts with the caller's `h-*` and loses.
      'h-[var(--table-head-h)] px-3 text-left align-middle text-xs font-semibold uppercase tracking-wide text-muted-foreground [--table-head-h:2.75rem] sm:[--table-head-h:1.75rem] [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      // See TableHead: the sm: step is a custom property so that a caller's
      // unprefixed `py-*` (e.g. `py-12` on empty-state rows) actually wins.
      'px-3 py-[var(--table-cell-py)] align-middle [--table-cell-py:0.625rem] sm:[--table-cell-py:0.25rem] [&:has([role=checkbox])]:pr-0',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';
